/**
 * Sequence Identifier Utilities
 * 
 * Handles the new sequence identifier format:
 * - "MST" (Master) - appears first
 * - "01" to "99" (numeric, zero-padded) - appears second
 * - "A" to "Z" (alphabetic) - appears last
 */

/** Valid sequence identifier regex: MST | 01-99 | A-Z */
export const SEQUENCE_ID_REGEX = /^(MST|0[1-9]|[1-9][0-9]|[A-Z])$/;

/** All valid numeric sequence identifiers */
export const NUMERIC_SEQUENCES = Array.from({ length: 99 }, (_, i) => 
  String(i + 1).padStart(2, '0')
);

/** All valid alphabetic sequence identifiers */
export const ALPHA_SEQUENCES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** All valid sequence identifiers in sort order */
export const ALL_SEQUENCES = ['MST', ...NUMERIC_SEQUENCES, ...ALPHA_SEQUENCES];

/**
 * Validates a sequence identifier
 */
export function isValidSequenceId(id: string): boolean {
  return SEQUENCE_ID_REGEX.test(id);
}

/**
 * Gets a numeric sort value for a sequence identifier.
 * MST = -1 (always first)
 * 01-99 = 1-99
 * A-Z = 100-125
 */
export function getSequenceSortValue(id: string): number {
  if (id === 'MST') return -1;
  
  // Check if it's a numeric sequence (01-99)
  const numMatch = id.match(/^(0[1-9]|[1-9][0-9])$/);
  if (numMatch) {
    return parseInt(id, 10);
  }
  
  // Check if it's an alphabetic sequence (A-Z)
  if (/^[A-Z]$/.test(id)) {
    return 100 + id.charCodeAt(0) - 64; // A=101, B=102, ... Z=126
  }
  
  // Unknown format, sort to end
  return 999;
}

/**
 * Comparator for sorting sequence identifiers.
 * Order: MST → 01-99 → A-Z
 */
export function compareSequenceIds(a: string, b: string): number {
  return getSequenceSortValue(a) - getSequenceSortValue(b);
}

/**
 * Formats a sequence identifier for display.
 * Numbers are already zero-padded, this just ensures consistency.
 */
export function formatSequenceId(id: string): string {
  return id; // Already formatted correctly
}

/**
 * Gets the sequence type for grouping in UI
 */
export function getSequenceType(id: string): 'special' | 'numeric' | 'alpha' {
  if (id === 'MST') return 'special';
  if (/^(0[1-9]|[1-9][0-9])$/.test(id)) return 'numeric';
  if (/^[A-Z]$/.test(id)) return 'alpha';
  return 'numeric'; // fallback
}

/** Alias for getSequenceSortValue for semantic clarity */
export const getSequenceWeight = getSequenceSortValue;
